import appConfig from "../config/app-config";

const { address: API_URL, key: API_KEY } = appConfig.apis.convertXmlApiEnv;

export interface BatchUploadProgress {
  currentBatch: number;
  totalBatches: number;
  processedFiles: number;
  totalFiles: number;
  successfulUploads: number;
  failedUploads: number;
  isComplete: boolean;
}

export interface BatchUploadResult {
  success: boolean;
  summary: {
    total_files: number;
    successful_uploads: number;
    failed_uploads: number;
    success_rate: number;
  };
  operation_code: string;
  errors: string[];
}

export const uploadInBatches = async (
  operationName: string,
  files: File[],
  batchSize: number = 30,
  onProgress?: (progress: BatchUploadProgress) => void
): Promise<BatchUploadResult> => {
  const totalFiles = files.length;
  const totalBatches = Math.ceil(totalFiles / batchSize);
  let successfulUploads = 0;
  let failedUploads = 0;
  const errors: string[] = [];
  let operationCode = '';

  // Verificar tamanho dos arquivos (limite de 50MB por arquivo)
  const maxFileSize = 50 * 1024 * 1024; // 50MB
  const oversizedFiles = files.filter(file => file.size > maxFileSize);
  if (oversizedFiles.length > 0) {
    throw new Error(`Arquivos muito grandes (>50MB): ${oversizedFiles.map(f => f.name).join(', ')}`);
  }

  for (let i = 0; i < totalBatches; i++) {
    const startIndex = i * batchSize;
    const endIndex = Math.min(startIndex + batchSize, totalFiles);
    const batchFiles = files.slice(startIndex, endIndex);

    console.log(`Processando lote ${i + 1}/${totalBatches} com ${batchFiles.length} arquivos`);

    try {
      const formData = new FormData();
      formData.append('operation_code', operationName);
      batchFiles.forEach((file) => formData.append('files', file));

      console.log(`Enviando requisição para ${API_URL}/upload`);
      console.log(`Lote ${i + 1} - Arquivos:`, batchFiles.map(f => f.name));

      const token = localStorage.getItem('authToken');

      // Timeout maior para arquivos grandes (5 minutos)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutos

      const response = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY || '',
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log(`Lote ${i + 1} - Status da resposta:`, response.status);

      const data = await response.json();
      console.log(`Lote ${i + 1} - Resposta:`, data);

      if (response.ok && data.status === 'success') {
        successfulUploads += batchFiles.length;
        if (!operationCode) {
          operationCode = data.operation_code || operationName;
        }
      } else {
        failedUploads += batchFiles.length;
        errors.push(`Lote ${i + 1}: ${data.error || data.message || 'Erro desconhecido'}`);
      }
    } catch (error: any) {
      failedUploads += batchFiles.length;
      if (error.name === 'AbortError') {
        errors.push(`Lote ${i + 1}: Timeout - arquivos muito grandes`);
      } else {
        errors.push(`Lote ${i + 1}: ${error.message || 'Erro de conexão'}`);
      }
    }

    // Atualizar progresso
    if (onProgress) {
      onProgress({
        currentBatch: i + 1,
        totalBatches,
        processedFiles: startIndex + batchFiles.length,
        totalFiles,
        successfulUploads,
        failedUploads,
        isComplete: i === totalBatches - 1
      });
    }

    // Pequena pausa entre lotes para não sobrecarregar a API
    if (i < totalBatches - 1) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  return {
    success: failedUploads === 0,
    summary: {
      total_files: totalFiles,
      successful_uploads: successfulUploads,
      failed_uploads: failedUploads,
      success_rate: Math.round((successfulUploads / totalFiles) * 100)
    },
    operation_code: operationCode,
    errors
  };
};
